import { useMutation } from "@apollo/client";
import { faCircle as faCircleEmpty } from "@fortawesome/pro-regular-svg-icons";
import {
  faChevronCircleUp,
  faCircleDot,
  faMinusCircle,
  faPencil,
} from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import DELETE_COMPANION_SESSION_ESTIMATE_ITEM from "@graphql/mutations/DeleteCompanionSessionEstimateItem";
import SET_COMPANION_SESSION_SELECTED_ESTIMATE_ITEM_CHOICE from "@graphql/mutations/SetCompanionSessionSelectedEstimateItemChoice";
import { useState } from "react";

function CompanionSessionEstimateItem({ item }) {
  const [deleteCompanionSessionEstimateItem] = useMutation(
    DELETE_COMPANION_SESSION_ESTIMATE_ITEM,
  );
  const [setCompanionSessionSelectedEstimateItemChoice] = useMutation(
    SET_COMPANION_SESSION_SELECTED_ESTIMATE_ITEM_CHOICE,
  );

  const [isExpanded, setIsExpanded] = useState(false);
  if (!item?.choices?.length) {
    return null;
  }
  const selectedChoice = item?.choices?.find(
    (choice) => choice.isSelected,
  );
  const unselectedChoices = item?.choices?.filter(
    (choice) => !choice.isSelected,
  );

  return (
    <div
      className={` px-2  ${
        isExpanded && "bg-white rounded-xl py-1 my-1"
      }`}
    >
      <div
        key={selectedChoice?.inventoryItemUuid}
        className={`flex justify-between items-center gap-2 my-1`}
      >
        {unselectedChoices?.length > 0 && (
          <>
            {isExpanded ? (
              <FontAwesomeIcon
                icon={faChevronCircleUp}
                size="lg"
                className="text-indigo-500 cursor-pointer"
                onClick={() => setIsExpanded(false)}
              />
            ) : (
              <FontAwesomeIcon
                icon={faPencil}
                size="lg"
                className="text-indigo-500 cursor-pointer"
                onClick={() => setIsExpanded(true)}
              />
            )}
          </>
        )}
        <div className="min-w-4">{item.quantity}</div>
        <span className="truncate flex-1">
          {selectedChoice?.inventoryItem?.description}
        </span>
        <span>
          $
          {(
            (selectedChoice?.inventoryItem?.priceCents || 0) / 100
          ).toFixed(2)}
        </span>
        <span
          className="text-red-500 cursor-pointer"
          onClick={() => {
            deleteCompanionSessionEstimateItem({
              variables: {
                companionSessionEstimateItemUuid:
                  item.companionSessionEstimateItemUuid,
              },
            });
          }}
        >
          <FontAwesomeIcon icon={faMinusCircle} size="lg" />
        </span>
      </div>
      {isExpanded &&
        item?.choices?.map((choice) => (
          <div
            key={choice.inventoryItemUuid}
            className="flex justify-between items-center gap-2 my-1"
          >
            {choice.isSelected ? (
              <FontAwesomeIcon
                icon={faCircleDot}
                size="lg"
                className="text-indigo-500 cursor-pointer"
              />
            ) : (
              <FontAwesomeIcon
                icon={faCircleEmpty}
                className="text-indigo-500 cursor-pointer"
                size="lg"
                onClick={async () => {
                  await setCompanionSessionSelectedEstimateItemChoice(
                    {
                      variables: {
                        companionSessionEstimateItemUuid:
                          item.companionSessionEstimateItemUuid,
                        inventoryItemUuid: choice.inventoryItemUuid,
                      },
                    },
                  );
                  setIsExpanded(false);
                }}
              />
            )}
            <span className="truncate flex-1">
              {choice.inventoryItem.description}
            </span>
            <span>
              ${(choice.inventoryItem.priceCents / 100).toFixed(2)}
            </span>
          </div>
        ))}
    </div>
  );
}

function CompanionSessionEstimate({ estimate }) {
  const calcTotal = (items) => {
    let total = 0;
    for (let item of items) {
      const selectedChoice = item.choices.find(
        (choice) => choice.isSelected,
      );
      if (!selectedChoice) {
        continue;
      }
      const itemQuantity = item.quantity;
      const price = selectedChoice.inventoryItem?.priceCents || 0;
      const dispensingFee =
        selectedChoice.inventoryItem?.dispensingFeeCents || 0;
      total += itemQuantity * (price + dispensingFee);
    }
    return Math.round(total) / 100;
  };

  const taxAmount = 13; // TODO: make this configurable
  const subtotal = calcTotal(estimate?.items);

  return (
    <div className="bg-indigo-500 bg-opacity-10 p-2 rounded-xl m-2 text-black">
      <div className="text-xl text-indigo-500 font-bold mb-2">
        Esti-Mate
        <sup>
          <small>TM</small>
        </sup>
      </div>
      {estimate?.items?.map((item) => (
        <CompanionSessionEstimateItem
          key={item.companionSessionEstimateItemUuid}
          item={item}
        />
      ))}
      <hr className="h-px bg-gray-300 border-0 my-2" />
      <div className="flex flex-1 justify-end font-semibold text-xs">
        Subtotal: ${subtotal.toFixed(2)}
      </div>
      <div className="flex flex-1 justify-end font-semibold text-xs">
        Tax (13%): $
        {(Math.round(subtotal * taxAmount) / 100).toFixed(2)}
      </div>
      <div className="flex flex-1 justify-end font-bold">
        Total: $
        {(Math.round(subtotal * (100 + taxAmount)) / 100).toFixed(2)}
      </div>
    </div>
  );
}

export function CompanionEstimateSection({ estimates }) {
  if (!estimates?.length) {
    return null;
  }
  const sortedEstimates = [...estimates].sort(
    (a, b) => new Date(a.createdAt) - new Date(b.createdAt),
  );

  return (
    <div>
      {sortedEstimates.map((estimate) => (
        <CompanionSessionEstimate
          key={estimate.companionSessionEstimateUuid}
          estimate={estimate}
        />
      ))}
    </div>
  );
}
